import { fromHex, getCrypto } from '../util.js';
export function localCsprng(numBytes) {
    const out = new Uint8Array(numBytes);
    getCrypto().getRandomValues(out);
    return out;
}
function getFetch(config) {
    const f = config.fetchImpl ?? globalThis.fetch;
    if (!f)
        throw new Error('No fetch implementation available.');
    return f;
}
async function withTimeout(p, ms) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);
    try {
        return await p(controller.signal);
    }
    finally {
        clearTimeout(timer);
    }
}
/**
 * NIST Randomness Beacon (v2). Public, keyless, quantum-seeded. Publishes a
 * 512-bit value every 60s — the same for everyone in a given minute, which is
 * its own kind of poetry: the cosmic pulse at the moment you asked.
 */
export async function fetchNistBeacon(numBytes, config = {}) {
    const fetchImpl = getFetch(config);
    try {
        const res = await withTimeout((signal) => fetchImpl('https://beacon.nist.gov/beacon/2.0/pulse/last', { signal }), config.timeoutMs ?? 6000);
        if (!res.ok)
            return { source: 'nist', ok: false, bytes: null, detail: `HTTP ${res.status}` };
        const json = (await res.json());
        const hex = json?.pulse?.outputValue;
        if (!hex)
            return { source: 'nist', ok: false, bytes: null, detail: 'no outputValue' };
        const full = fromHex(hex);
        return { source: 'nist', ok: true, bytes: full.subarray(0, numBytes) };
    }
    catch (err) {
        return { source: 'nist', ok: false, bytes: null, detail: String(err) };
    }
}
/**
 * ANU Quantum Random Numbers — vacuum fluctuations. The classic public
 * endpoint is now rate-limited and may require an API key; we pass one if
 * provided and fail gracefully otherwise.
 */
export async function fetchAnu(numBytes, config = {}) {
    const fetchImpl = getFetch(config);
    try {
        const url = `https://qrng.anu.edu.au/API/jsonI.php?length=${numBytes}&type=uint8`;
        const headers = {};
        if (config.anuApiKey)
            headers['x-api-key'] = config.anuApiKey;
        const res = await withTimeout((signal) => fetchImpl(url, { headers, signal }), config.timeoutMs ?? 6000);
        if (!res.ok)
            return { source: 'anu', ok: false, bytes: null, detail: `HTTP ${res.status}` };
        const json = (await res.json());
        if (!json?.success || !Array.isArray(json.data)) {
            return { source: 'anu', ok: false, bytes: null, detail: 'unsuccessful response' };
        }
        return { source: 'anu', ok: true, bytes: Uint8Array.from(json.data.slice(0, numBytes)) };
    }
    catch (err) {
        return { source: 'anu', ok: false, bytes: null, detail: String(err) };
    }
}
/** RANDOM.ORG atmospheric noise (JSON-RPC v4). Requires an API key. */
export async function fetchRandomOrg(numBytes, config = {}) {
    const fetchImpl = getFetch(config);
    if (!config.randomOrgApiKey) {
        return { source: 'random.org', ok: false, bytes: null, detail: 'no API key' };
    }
    try {
        const body = {
            jsonrpc: '2.0',
            method: 'generateIntegers',
            params: { apiKey: config.randomOrgApiKey, n: numBytes, min: 0, max: 255, replacement: true },
            id: 1,
        };
        const res = await withTimeout((signal) => fetchImpl('https://api.random.org/json-rpc/4/invoke', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(body),
            signal,
        }), config.timeoutMs ?? 6000);
        if (!res.ok)
            return { source: 'random.org', ok: false, bytes: null, detail: `HTTP ${res.status}` };
        const json = (await res.json());
        const data = json?.result?.random?.data;
        if (!Array.isArray(data)) {
            return { source: 'random.org', ok: false, bytes: null, detail: 'no data' };
        }
        return { source: 'random.org', ok: true, bytes: Uint8Array.from(data) };
    }
    catch (err) {
        return { source: 'random.org', ok: false, bytes: null, detail: String(err) };
    }
}
/**
 * Attempt every configured source concurrently and always include the local
 * CSPRNG. Returns one result per attempted source (failures included, so the
 * UI can show what answered the call).
 */
export async function gatherEntropy(numBytes, config = {}) {
    const requested = config.sources ?? ['nist', 'csprng'];
    const set = new Set(requested);
    set.add('csprng'); // guaranteed fallback, always present
    const jobs = [];
    for (const source of set) {
        switch (source) {
            case 'nist':
                jobs.push(fetchNistBeacon(numBytes, config));
                break;
            case 'anu':
                jobs.push(fetchAnu(numBytes, config));
                break;
            case 'random.org':
                jobs.push(fetchRandomOrg(numBytes, config));
                break;
            case 'csprng':
                jobs.push(Promise.resolve({ source: 'csprng', ok: true, bytes: localCsprng(numBytes) }));
                break;
        }
    }
    return Promise.all(jobs);
}
//# sourceMappingURL=qrng.js.map